import axiosInstance from "../../services/axiosInstance";
import CardsView from "./views/cards_view";
import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useModalStore } from "../../store/modalStore";
import { motion } from "framer-motion";

import {
  Box,
  Card,
  TablePagination,
  Typography,
  useTheme,
  Skeleton,
} from "@mui/material";
export interface StoreData {
  storeState: string;
}

export interface CorpData {
  legalCorpName: string;
}

export interface Entry {
  id: number;
  storeName: string;
  storeCity: string;
  storeInformationData: StoreData;
  corporationInformationData: CorpData;
  licenseGeneratedDate: string;
  leaseGeneratedDate: string;
  applicationGeneratedDate: string;
  markedReceivedDate: string;
  docsGeneratedBy: string;
  markedReceivedBy: string;
  emailReceived: boolean;
  onDelete: void;
}

const EntriesListingPage: React.FC = () => {
  // States
  const isActionsLoading = useModalStore((state) => state.isActionsLoading);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [totalEntries, setTotalEntries] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  // Hooks
  const theme = useTheme();

  // API calls
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    const offset = page * rowsPerPage;
    try {
      const response = await axiosInstance.get(
        `/api/v1/retail?limit=${rowsPerPage}&offSet=${offset}`
      );

      if (response.data && response.data.data) {
        setEntries(response.data.data.entries);
        setTotalEntries(response.data.data.totalRecords);
      } else {
        console.error("Invalid API response structure:", response.data);
      }
    } catch (error: any) {
      if (error.status === 404) {
        return toast.info("No entries for Retail application.");
      }
      toast.error("Error fetching entries. Please try again.");
      console.error("Failed to fetch entries:", error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Handlers
  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    // Scroll to the top of the page with smooth scrolling
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to the first page when rows per page changes
    // Scroll to the top of the page with smooth scrolling
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteStore = (entryId: number) => {
    setEntries((prevEntries) =>
      prevEntries.filter((entry) => entry.id !== entryId)
    );
  };

  // Skeleton loading for each card
  const renderSkeletonCards = () => {
    const skeletonCards = [];
    for (let i = 0; i < rowsPerPage; i++) {
      skeletonCards.push(
        <Box
          key={i}
          sx={{ display: "flex", flexDirection: "column", width: "100%" }}
        >
          <Skeleton
            variant="rectangular"
            width="100%"
            height={200}
            sx={{ borderRadius: 3 }}
          />
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
        </Box>
      );
    }
    return skeletonCards;
  };

  return (
    <>
      <Box
        sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
        m={4}
      >
        <Card
          sx={{
            p: 1,
            margin: "auto",
            width: "100%",
            backgroundColor: "#343434",
            color: "white",
            textAlign: "left",
          }}
        >
          <motion.div
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, type: "spring", stiffness: 120 }}
            className="text-xl font-medium"
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: "bold",
                fontSize: "2rem", // Font size for the heading
                textShadow: "1px 1px 2px rgba(0, 0, 0, 0.15)",
              }}
            >
              Retail Applications Listing
            </Typography>
          </motion.div>
        </Card>
        {/* Show skeletons when loading */}
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} mt={3}>
            {renderSkeletonCards()}
          </Box>
        ) : entries.length > 0 ? (
          <motion.text
            initial={{ filter: "blur(20px)", opacity: 0 }}
            animate={{ filter: "blur(0px)", opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <CardsView entries={entries} onDelete={handleDeleteStore} />
          </motion.text>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              height: "60vh", // Adjust for better spacing
              padding: 2,
            }}
          >
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Oops! No retail applications found.
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              We couldn't find any entries for the current public form.
            </Typography>
          </Box>
        )}
        {/* Sticky Pagination Component */}
        <Box sx={{ flexGrow: 1 }} />{" "}
        {/* This ensures that the content above can scroll */}
      </Box>
      {entries.length && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            backgroundColor: theme.palette.background.paper,
            zIndex: 1, // Ensure it's on top of other content
            boxShadow: "0 1px 5px rgba(0, 0, 0, 0.1)", // Optional shadow for better visibility
          }}
        >
          <TablePagination
            disabled={isActionsLoading}
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalEntries}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      )}
    </>
  );
};

export default EntriesListingPage;
